import type { ChangeDetectorRef } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import type { EventBusService } from '@ups/xplat/core';
import { Observable, filter, merge, takeUntil } from 'rxjs';
import { DynamicEventBusTypes } from './dynamic-event-bus-types';

/**
 * WI 32220: dynamicFormResetState used to be a bare global broadcast, so a reset on one form
 * reset every mounted dynamic control on every page (the checkbox even writes false into its
 * control). A scoped reset carries the form it applies to, and only controls rendered inside
 * that form (at any nesting depth) respond.
 */
export interface DynamicFormResetEvent {
  scope: AbstractControl;
}

type ResetBus = Pick<EventBusService, 'emit' | 'observe'>;

function isScopedReset(event: unknown): event is DynamicFormResetEvent {
  return !!event && typeof event === 'object' && 'scope' in (event as object);
}

/**
 * True when a reset applies to a control rendered in `group`. A payload with no scope (no
 * payload, or resetControlState's `true`) is an explicit GLOBAL reset and applies everywhere.
 */
export function isFormResetFor(
  event: unknown,
  group: AbstractControl | null | undefined
): boolean {
  if (!isScopedReset(event)) {
    return true;
  }
  for (let c: AbstractControl | null = group ?? null; c; c = c.parent) {
    if (c === event.scope) {
      return true;
    }
  }
  return false;
}

/** Emit a reset for the controls rendered inside `form` only. No form, no reset. */
export function emitScopedFormReset(
  eventBus: ResetBus,
  form: AbstractControl | null | undefined
): void {
  if (form) {
    const event: DynamicFormResetEvent = { scope: form };
    eventBus.emit(DynamicEventBusTypes.dynamicFormResetState, event);
  }
}

/** The resets that apply to a control rendered in the group `getGroup` returns at emit time. */
export function observeFormReset(
  eventBus: ResetBus,
  getGroup: () => AbstractControl | null | undefined
): Observable<unknown> {
  return eventBus
    .observe<unknown>(DynamicEventBusTypes.dynamicFormResetState)
    .pipe(filter((event) => isFormResetFor(event, getGroup())));
}

/** Announce an in-place config write that is not a reset (e.g. a validation flag), scoped to `form`. */
export function emitScopedConfigWritten(
  eventBus: ResetBus,
  form: AbstractControl | null | undefined
): void {
  if (form) {
    const event: DynamicFormResetEvent = { scope: form };
    eventBus.emit(DynamicEventBusTypes.dynamicConfigWritten, event);
  }
}

/**
 * Bug 35607: a form reset, resetControlState and other in-place writers (emitScopedConfigWritten)
 * write config, then emit. A vcRef-created OnPush view is skipped by its Default container once
 * clean, so ngDoCheck never sees those writes; marking on the events repaints it.
 */
export function markOnConfigWrite(
  eventBus: ResetBus,
  getGroup: () => AbstractControl | null | undefined,
  cdr: Pick<ChangeDetectorRef, 'markForCheck'> | null | undefined,
  until$: Observable<unknown>
): void {
  const written$ = eventBus
    .observe<unknown>(DynamicEventBusTypes.dynamicConfigWritten)
    .pipe(filter((event) => isFormResetFor(event, getGroup())));
  merge(observeFormReset(eventBus, getGroup), written$)
    .pipe(takeUntil(until$))
    .subscribe(() => cdr?.markForCheck());
}
